import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, delay, BehaviorSubject, map } from 'rxjs';
import { environment } from '@env/environment';
import { 
  SubscriptionPlan, 
  UserSubscription, 
  PaymentMethod, 
  BillingInvoice, 
  BillingInfo,
  SubscriptionDashboardData 
} from '@app/core/models/subscription.model';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private URL: string = environment.apiUrl + 'subscriptions/';

  // Initial Mocks
  private _plans: SubscriptionPlan[] = [
    {
      id: 'plan_basic',
      name: 'Plan Básico',
      price: 0,
      currency: 'MXN',
      interval: 'month',
      features: ['Búsqueda de fletes', 'Perfil de empresa', 'Soporte estándar'],
      isPopular: false
    },
    {
      id: 'plan_pro',
      name: 'Plan Profesional',
      price: 499,
      currency: 'MXN',
      interval: 'month',
      features: ['Todo en Básico', 'Publicación ilimitada', 'Gps integrado', 'Soporte 24/7'],
      isPopular: true
    },
    {
      id: 'plan_enterprise',
      name: 'Plan Enterprise',
      price: 999,
      currency: 'MXN',
      interval: 'month',
      features: ['Todo en Pro', 'Reportes avanzados', 'API Access', 'Gestor de cuenta'],
      isPopular: false
    }
  ];

  private _subscription: UserSubscription = {
    status: 'active',
    currentPeriodEnd: new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    cancelAtPeriodEnd: false,
    plan: this._plans[1] // Pro plan by default
  };

  private _paymentMethods: PaymentMethod[] = [
    {
      id: 'pm_1',
      brand: 'visa',
      last4: '4242',
      expMonth: 12,
      expYear: 2026,
      isDefault: true,
      isExpired: false
    }
  ];

  private _billingInfo: BillingInfo = {
    taxId: 'ABC123456XYZ',
    legalName: 'Transportes Modernos S.A. de C.V.',
    address: 'Av. Reforma 123, Col. Juárez',
    zipCode: '06600'
  };

  private _invoices: BillingInvoice[] = [
    {
      id: 'inv_1',
      date: new Date().toISOString(),
      amount: 499,
      tax: 79.84,
      currency: 'MXN',
      status: 'paid',
      pdfUrl: '#'
    }
  ];

  private dashboardDataSubject = new BehaviorSubject<SubscriptionDashboardData>({
    subscription: this._subscription,
    paymentMethods: this._paymentMethods,
    billingInfo: this._billingInfo,
    invoices: this._invoices
  });

  constructor(@Inject(HttpClient) private http: HttpClient) {}

  getPlans(): Observable<SubscriptionPlan[]> {
    return of(this._plans).pipe(delay(500));
  }

  getDashboardData(): Observable<SubscriptionDashboardData> {
    return this.dashboardDataSubject.asObservable().pipe(delay(500));
  }

  updateBillingInfo(info: BillingInfo): Observable<boolean> {
    this._billingInfo = { ...info };
    this.refreshDashboard();
    return of(true).pipe(delay(500));
  }

  updateSubscription(planId: string): Observable<boolean> {
    const newPlan = this._plans.find(p => p.id === planId);
    if (newPlan) {
      this._subscription = {
        ...this._subscription,
        plan: newPlan,
        status: 'active'
      };
      // Add new invoice mock
      this._invoices.unshift({
        id: `inv_${Date.now()}`,
        date: new Date().toISOString(),
        amount: newPlan.price,
        tax: newPlan.price * 0.16,
        currency: newPlan.currency,
        status: 'paid',
        pdfUrl: '#'
      });
      this.refreshDashboard();
      return of(true).pipe(delay(1000));
    }
    return of(false);
  }

  cancelSubscription(): Observable<boolean> {
    this._subscription = {
      ...this._subscription,
      cancelAtPeriodEnd: true,
      status: 'canceled'
    };
    this.refreshDashboard();
    return of(true).pipe(delay(1000));
  }

  addPaymentMethod(cardData: any): Observable<boolean> {
    const newPm: PaymentMethod = {
      id: `pm_${Date.now()}`,
      brand: cardData.number.startsWith('4') ? 'visa' : 'mastercard',
      last4: cardData.number.slice(-4),
      expMonth: parseInt(cardData.expiry.split('/')[0]),
      expYear: parseInt(cardData.expiry.split('/')[1]) + 2000,
      isDefault: this._paymentMethods.length === 0,
      isExpired: false
    };
    this._paymentMethods.push(newPm);
    this.refreshDashboard();
    return of(true).pipe(delay(1000));
  }

  deletePaymentMethod(id: string): Observable<boolean> {
    if (this._paymentMethods.length <= 1) {
      return of(false); // Cannot delete the last one
    }
    const index = this._paymentMethods.findIndex(pm => pm.id === id);
    if (index !== -1) {
      const wasDefault = this._paymentMethods[index].isDefault;
      this._paymentMethods.splice(index, 1);
      if (wasDefault && this._paymentMethods.length > 0) {
        this._paymentMethods[0].isDefault = true;
      }
      this.refreshDashboard();
      return of(true).pipe(delay(500));
    }
    return of(false);
  }

  setDefaultPaymentMethod(id: string): Observable<boolean> {
    this._paymentMethods.forEach(pm => pm.isDefault = (pm.id === id));
    this.refreshDashboard();
    return of(true).pipe(delay(500));
  }

  private refreshDashboard() {
    this.dashboardDataSubject.next({
      subscription: { ...this._subscription },
      paymentMethods: [...this._paymentMethods],
      billingInfo: { ...this._billingInfo },
      invoices: [...this._invoices]
    });
  }
}
